import { ISearchInput } from '../types';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { GET_ENTITY_CATEGORIES } from '../../../../utils/apollo/entityCategories';
import {
  IEntityCategoryFieldsFragment,
  IGetEntityCategoriesQuery,
  IGetEntityCategoriesQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import React, { useState } from 'react';
import { ReactSelectMenu } from '../../../../components/Dropdown/StyledReactSelect';
import { MultiValue } from 'react-select';
import { ControlWithSearchIcon } from '../../../../components/Dropdown/StyledReactSelect/SelectUtils';
import { SvgIcon } from '../../../../components/Icon';
import { closeMenuOnScrollOutside } from '../../../../components/Dropdown/StyledReactSelect/utils';
import * as AdvancedSearchMenuUI from './AdvancedSearchMenuUI';
import Pill from '../../../../components/Pill';
import { overflowingStyles } from '../../../../styles/shareableStyles';

interface IProps {
  searchInput: ISearchInput;
  setSearchInput: (searchInput: ISearchInput, isLoadMore: boolean) => void;
}

const TruncatedText = styled.span`
  display: block;
  max-width: 100%;
  ${overflowingStyles};
`;

const TagSearchOption = ({ searchInput, setSearchInput }: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<IEntityCategoryFieldsFragment[]>(
    [],
  );
  const onFetchCategories = ({
    entityCategories,
  }: IGetEntityCategoriesQuery) => {
    setCategories(entityCategories ? entityCategories.entities : []);
  };
  const { loading } = useQuery<
    IGetEntityCategoriesQuery,
    IGetEntityCategoriesQueryVariables
  >(GET_ENTITY_CATEGORIES, {
    variables: {
      term: searchTerm,
      schemaIds: searchInput.schemaIds,
      includes: searchInput.categoryIds,
      pagination: { first: 50, after: 0 },
    },
    onCompleted: onFetchCategories,
  });

  const onSelectCategory = (
    newOptions: MultiValue<IEntityCategoryFieldsFragment>,
  ) => {
    if (!newOptions) {
      return;
    }
    const newCategoryIds = newOptions.map((option) => option.id);
    setSearchInput({ ...searchInput, categoryIds: newCategoryIds }, false);
  };

  const onRemoveCategory = (removeCategoryId: string) => {
    setSearchInput(
      {
        ...searchInput,
        categoryIds: searchInput.categoryIds.filter(
          (id) => id !== removeCategoryId,
        ),
      },
      false,
    );
  };
  return (
    <AdvancedSearchMenuUI.SectionContainer>
      <AdvancedSearchMenuUI.SectionHeading>
        Tags
      </AdvancedSearchMenuUI.SectionHeading>
      {searchInput.categoryIds.length > 0 && (
        <AdvancedSearchMenuUI.SectionList>
          {categories
            .filter((category) => searchInput.categoryIds.includes(category.id))
            .map((category) => {
              return (
                <Pill
                  key={category.id}
                  onClick={() => onRemoveCategory(category.id)}
                >
                  <TruncatedText>
                    {category.name} ({category.schema.name})
                  </TruncatedText>
                  <SvgIcon
                    name="icon-x-bold"
                    width={16}
                    height={16}
                    customFill="currentColor"
                  />
                </Pill>
              );
            })}
        </AdvancedSearchMenuUI.SectionList>
      )}
      <ReactSelectMenu
        options={categories}
        getOptionLabel={(option) => `${option.name} (${option.schema.name})`}
        getOptionValue={(option) => option.id}
        onChange={onSelectCategory}
        value={categories.filter((category) =>
          searchInput.categoryIds.includes(category.id),
        )}
        isMulti={true}
        controlShouldRenderValue={false}
        hideSelectedOptions
        components={{
          Control: ControlWithSearchIcon,
        }}
        isLoading={loading}
        isSearchable
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        placeholder={'Search for Tags'}
        menuPortalTarget={document.body}
        // close menu if scroll outside the menu
        closeMenuOnScroll={closeMenuOnScrollOutside}
        styles={{
          control: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
          menu: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
        }}
        blurInputOnSelect={true}
      />
    </AdvancedSearchMenuUI.SectionContainer>
  );
};
export default TagSearchOption;
